import {
  bindThemeParamsCssVars,
  bindViewportCssVars,
  closeMiniApp,
  expandViewport,
  hapticFeedbackImpactOccurred,
  hapticFeedbackNotificationOccurred,
  hapticFeedbackSelectionChanged,
  hideBackButton,
  type ImpactHapticFeedbackStyle,
  init,
  type MainButtonState,
  mountBackButton,
  mountMainButton,
  mountThemeParamsSync,
  mountViewport,
  type NotificationHapticFeedbackType,
  offBackButtonClick,
  offMainButtonClick,
  onBackButtonClick,
  onMainButtonClick,
  openTelegramLink,
  requestWriteAccess,
  retrieveLaunchParams,
  retrieveRawInitData,
  setMainButtonParams,
  shareMessage,
  shareStory,
  showBackButton,
} from '@telegram-apps/sdk-react';
import {
  parseLinkParam,
  type ReferralChannel,
} from '@veylme/shared';

/**
 * Обёртка над Telegram Mini Apps SDK v3.x.
 *
 * SDK 3.x использует scoped signals и единую функцию `init` из основного пакета
 * `@telegram-apps/sdk` (реэкспортируется через `@telegram-apps/sdk-react`).
 *
 * См. node_modules/@telegram-apps/sdk/dist/dts/index.d.ts (init, retrieveRawInitData,
 * retrieveLaunchParams, mockTelegramEnv).
 */

/**
 * Инициализация SDK. Должна вызываться один раз в `main.tsx` до рендера App.
 *
 * Если запускаемся вне Telegram-клиента (local dev), перед `initSDK()` нужно
 * вызвать `applyMockTelegramEnv()` из `./mock.ts` — иначе `retrieveLaunchParams`
 * выбросит `LaunchParamsRetrieveError`.
 *
 * Возвращает cleanup-функцию (вызвать на unmount, если нужно).
 */
export function initSDK(): VoidFunction {
  return init({ acceptCustomStyles: true });
}

/**
 * Достать сырой initData (строка query-string), который должен идти в API как
 * `Authorization: tma <raw>`. CANON D1: клиент НЕ парсит подпись.
 *
 * Возвращает `undefined`, если launch params не доступны / не содержат init data
 * (например, mock без `tgWebAppData` или окружение не Telegram-like).
 */
export function getRawInitData(): string | undefined {
  try {
    return retrieveRawInitData();
  } catch {
    return undefined;
  }
}

/**
 * Проверить, является ли текущий пользователь Telegram Premium-подписчиком.
 *
 * Читает `is_premium` из распарсенного initData user (опциональный boolean,
 * сверено по @telegram-apps/transformers init-data schema).
 *
 * Возвращает `false` в любом из случаев: вне Telegram, ошибка SDK, поле отсутствует
 * (у не-Premium пользователей Telegram не включает is_premium в initData).
 *
 * CANON G3: никогда не бросает.
 */
export function isPremiumUser(): boolean {
  try {
    const lp = retrieveLaunchParams();
    // retrieveLaunchParams() без аргумента возвращает snake_case (RetrieveLPResult).
    // Поле is_premium — опциональный boolean в user initData.
    return lp.tgWebAppData?.user?.is_premium === true;
  } catch {
    return false;
  }
}

/**
 * Telegram-id текущего пользователя как строка, или `undefined` вне Telegram.
 *
 * Нужен ровно для того, чтобы разделить локальные черновики между аккаунтами на
 * одном устройстве (work-038, `lib/launchContext.ts`): в Telegram можно
 * переключить аккаунт, а `localStorage` общий на источник. Без разделения
 * недописанное анонимное сообщение одного аккаунта подставилось бы в поле
 * ввода другому.
 *
 * Это чтение поля уже разобранного initData, как и `isPremiumUser()` выше, —
 * НЕ проверка подлинности. CANON D1/SP3: подпись валидирует только сервер,
 * и никакого решения о доступе на этом значении не принимается.
 */
export function getTelegramUserId(): string | undefined {
  try {
    const lp = retrieveLaunchParams();
    const id: unknown = lp.tgWebAppData?.user?.id;
    if (typeof id === 'number' && Number.isFinite(id)) return String(id);
    if (typeof id === 'string' && id !== '') return id;
    return undefined;
  } catch {
    return undefined;
  }
}

/**
 * Достать `start_param` (botstart deep-link payload), переданный через
 * `t.me/<bot>/<app>?startapp=<token>` или `/start <token>` в боте.
 *
 * SDK 3.x хранит его в верхнеуровневом launch param `tgWebAppStartParam`
 * (не путать со `start_param` внутри `tgWebAppData` — это отдельный механизм).
 *
 * Возвращает `undefined`, если launch params недоступны или start_param пуст.
 */
export function getStartParam(): string | undefined {
  try {
    const lp = retrieveLaunchParams();
    return lp.tgWebAppStartParam;
  } catch {
    return undefined;
  }
}

/**
 * Pure-функция парсинга deep-link payload в режим запуска приложения.
 *
 * work-035 (ADR 0014): делегирует единой точке разбора `@veylme/shared/parseLinkParam`
 * (legacy `box_`/`boxs_`/`boxc_` + полный token, новый короткий код `b<s|c|d><code>`,
 * рекламная метка `a<code>`). Маппит union в режим запуска:
 *   - legacy (`{kind:'box', token, channel}`) → `{ mode:'ASK', token, channel }` —
 *     token уже полный, резолв не нужен, ASK-поток стартует сразу;
 *   - новый короткий код (`{kind:'box', code, channel}`) → `{ mode:'ASK_RESOLVING',
 *     code, channel }` — `App.tsx` обменивает код на полный token через
 *     `GET /boxes/link/:code` ОДИН раз на bootstrap (§4 work-035 плана), дальше
 *     ASK-поток идёт с token как раньше (`min(8)`/`TOKEN_REGEX` соблюдены);
 *   - рекламная метка (`{kind:'ad', code}`) → `{ mode:'AD', code }` — НЕ ask-
 *     контекст, App вызывает `POST /users/ad-source` (fail-soft) и идёт в INBOX;
 *   - `null` (невалид / пусто / неизвестный формат) → `{ mode:'INBOX' }`.
 *
 * CANON G3/Q3: НИКОГДА не падает, любой невалидный input → INBOX fallback.
 */
export type StartParamResult =
  | { mode: 'ASK'; token: string; channel: ReferralChannel }
  | { mode: 'ASK_RESOLVING'; code: string; channel: ReferralChannel }
  | { mode: 'AD'; code: string }
  | { mode: 'INBOX' };

export function parseStartParam(raw: string | undefined): StartParamResult {
  const parsed = parseLinkParam(raw);
  if (!parsed) return { mode: 'INBOX' };
  if (parsed.kind === 'ad') return { mode: 'AD', code: parsed.code };
  // parsed.kind === 'box' — либо legacy (token уже полный), либо новый короткий код.
  if ('token' in parsed) return { mode: 'ASK', token: parsed.token, channel: parsed.channel };
  return { mode: 'ASK_RESOLVING', code: parsed.code, channel: parsed.channel };
}

/**
 * Привязать CSS-vars от Telegram `themeParams` к `:root` (паттерн `--tg-theme-{key}`,
 * автоматическое обновление при смене темы пользователем).
 *
 * Реализация: SDK 3.x требует сначала смонтировать `themeParams` компонент
 * (`mountThemeParamsSync`), затем вызвать `bindCssVars()`. Обе функции —
 * `SafeWrapped`, поэтому используем `.ifAvailable()` чтобы НЕ падать в
 * не-Telegram окружении (например, vitest без mockTelegramEnv).
 *
 * Должна вызываться ПОСЛЕ `initSDK()`. Возвращает cleanup, который отвяжет
 * vars (для symmetry; в Mini App жизненный цикл = жизнь страницы, cleanup
 * скорее декоративный).
 */
export function bindThemeCssVars(): VoidFunction | undefined {
  // mountSync кидает FunctionNotAvailableError если SDK не инициализирован
  // или окружение не Telegram. ifAvailable() — проверка перед вызовом.
  mountThemeParamsSync.ifAvailable();
  const result = bindThemeParamsCssVars.ifAvailable();
  // ifAvailable возвращает [called: true, data] | [called: false]
  if (result[0]) return result[1];
  return undefined;
}

/**
 * Смонтировать viewport, развернуть его на всю высоту и привязать CSS-vars
 * (`--tg-viewport-*`, content/safe-area insets) к `:root`. Веха 13.1.
 *
 * Порядок строгий: `mount` (async, `AbortablePromise<void>`) → после резолва
 * `expand()` (sync) + `bindViewportCssVars()` (sync). Все три — `SafeWrapped`,
 * поэтому через `.ifAvailable()`: вне Telegram (local dev без mockEnv, vitest)
 * mount недоступен → no-op, приложение не падает. Сверено по
 * viewport/{mounting,expand,css-vars}.d.ts (SDK 3.11.8, Веха 13.0).
 *
 * Должна вызываться ПОСЛЕ `initSDK()`. Сами CSS-vars потребляет layout
 * (content-safe-area, Веха 13.4); базовый `env(safe-area-inset-*)` в #root
 * остаётся фолбэком, если mount не случился.
 */
export function initViewport(): void {
  const mountResult = mountViewport.ifAvailable();
  // ifAvailable → [true, AbortablePromise<void>] | [false]
  if (!mountResult[0]) return;
  void mountResult[1]
    .then(() => {
      expandViewport.ifAvailable();
      bindViewportCssVars.ifAvailable();
    })
    .catch(() => {
      // mount прерван / окружение не Telegram — CSS env() safe-area остаётся фолбэком
    });
}

/**
 * Открыть нативный Story-editor с media-карточкой (фолбэк F1/F2 §6.3).
 *
 * CANON G2/I4: вызываем ТОЛЬКО из обработчика user-gesture (onClick), НЕ из useEffect.
 * Это наш инвариант (UX + единый паттерн со всеми share-методами). Сверка §16.5
 * (Веха 12, 2026-05-31): официальные docs core.telegram.org/bots/webapps НЕ требуют
 * user-gesture именно для `shareToStory`/`shareMessage` (фраза «only in response to
 * user interaction» относится к `openLink` и др.) — но из onClick вызывать безопасно
 * и правильно, инвариант G2 сохраняем.
 *
 * Сигнатура сверена с node_modules .../shareStory.d.ts (Mini Apps v7.8) + docs
 * core.telegram.org/bots/webapps: `shareStory(mediaUrl, { text?, widgetLink? })`.
 * `shareStory` синхронный (`void`) — успех значит «editor открыт», НЕ «опубликовано»
 * (в SDK нет события share_to_story_published). `widgetLink` кликабелен ТОЛЬКО у
 * Premium-юзеров; для остальных он no-op, поэтому при пустом url опускаем его
 * целиком (фолбэк — бренд вшит в саму карту, CANON A2/F1).
 */
export function shareToStoryOrUnsupported(
  mediaUrl: string,
  opts: { text: string; widgetLinkUrl: string; widgetLinkName: string },
): 'shared' | 'unsupported' | 'failed' {
  if (!shareStory.isAvailable()) return 'unsupported';
  try {
    shareStory(mediaUrl, {
      text: opts.text,
      widgetLink: opts.widgetLinkUrl
        ? { url: opts.widgetLinkUrl, name: opts.widgetLinkName }
        : undefined,
    });
    return 'shared';
  } catch {
    return 'failed';
  }
}

/**
 * Открыть чат-селектор для пересылки prepared inline message (фолбэк F3 §6.3).
 *
 * CANON G2/I4: только из onClick. Сигнатура сверена с .../shareMessage.d.ts
 * (Mini Apps v8.0, AbortablePromise<void>) + docs. БЕЗ Premium-гейта — единственный
 * кликабельный путь шеринга для не-Premium юзеров.
 *
 * `preparedMessageId` — id от `POST /share/prepare` (Bot API savePreparedInlineMessage).
 * Промис резолвится при успешной отправке, бросает `ShareMessageError` при провале.
 */
export async function shareMessageOrUnsupported(
  preparedMessageId: string,
): Promise<'shared' | 'unsupported' | 'failed'> {
  if (!shareMessage.isAvailable()) return 'unsupported';
  try {
    await shareMessage(preparedMessageId);
    return 'shared';
  } catch {
    return 'failed';
  }
}

/**
 * Запросить у юзера право бота писать ему (write-access, Mini Apps v6.9).
 *
 * CANON G2/I4: только из onClick. Сигнатура сверена с .../requestWriteAccess.d.ts +
 * docs. SDK-тип статуса — `'allowed' | string` (а не строгий `'allowed' | 'cancelled'`),
 * поэтому нормализуем: всё, что не `'allowed'`, считаем отказом.
 */
export async function requestWriteAccessSafe(): Promise<
  'allowed' | 'cancelled' | 'unsupported'
> {
  if (!requestWriteAccess.isAvailable()) return 'unsupported';
  try {
    const status = await requestWriteAccess();
    return status === 'allowed' ? 'allowed' : 'cancelled';
  } catch {
    return 'unsupported';
  }
}

/* ─────────────────────────────────────────────────────────────────────────
 * work-038 — перезапуск приложения с экрана истёкшей сессии.
 *
 * Telegram выдаёт `initData` один раз при запуске и не умеет его обновлять,
 * поэтому единственное лечение протухшего пропуска — открыть мини-апп заново.
 * Обёртки тонкие: `isAvailable()` + try/catch + union-строка, как у соседей
 * выше. Решение «что делать дальше» принимает `screens/SessionExpiredScreen`.
 * ──────────────────────────────────────────────────────────────────────── */

/**
 * Доступно ли закрытие мини-аппа. Нужно ЗАРАНЕЕ, до отрисовки кнопки: вне
 * Telegram обе операции недоступны, и живая кнопка, которая по нажатию не
 * делает ничего, — тупик хуже честного текста «закройте приложение сами».
 */
export function closeMiniAppAvailable(): boolean {
  return closeMiniApp.isAvailable();
}

/** Доступно ли открытие `t.me`-ссылки. См. коммент к `closeMiniAppAvailable`. */
export function openTelegramLinkAvailable(): boolean {
  return openTelegramLink.isAvailable();
}

/**
 * Закрыть мини-апп. Сигнатура сверена с установленным SDK 3.11.8:
 * `close as closeMiniApp`, `(returnBack?: boolean) => void`
 * (.../scopes/components/mini-app/methods.d.ts:46).
 */
export function closeMiniAppSafe(): 'closed' | 'unsupported' | 'failed' {
  if (!closeMiniApp.isAvailable()) return 'unsupported';
  try {
    closeMiniApp();
    return 'closed';
  } catch {
    return 'failed';
  }
}

/**
 * Открыть `t.me`-ссылку внутри Telegram. По документации SDK мини-апп при этом
 * закрывается — то есть один вызов и закрывает текущий (протухший) запуск, и
 * открывает новый по ссылке с сохранённым контекстом. Это и есть возврат.
 *
 * Сигнатура сверена с установленным SDK 3.11.8:
 * `openTelegramLink: SafeWrapped<(url: string | URL) => void>`
 * (.../scopes/utils/exports/openTelegramLink.d.ts) — бросает `InvalidArgumentsError`
 * на кривом URL, поэтому вызов обязательно в try/catch.
 */
export function openTelegramLinkSafe(url: string): 'opened' | 'unsupported' | 'failed' {
  if (!openTelegramLink.isAvailable()) return 'unsupported';
  try {
    openTelegramLink(url);
    return 'opened';
  } catch {
    return 'failed';
  }
}

/* ─────────────────────────────────────────────────────────────────────────
 * Веха 13.2 — обёртки над нативными MainButton / BackButton / haptic feedback.
 *
 * Все нативные методы SDK 3.x — `SafeWrapped` → дёргаем через `.isAvailable()`,
 * чтобы код не падал вне Telegram (local dev mock без поддержки кнопок, vitest).
 * React-логика lifecycle (mount/cleanup/StrictMode/stale-closure) — НЕ здесь, а
 * в хуках `lib/nativeUi.ts`; здесь только тонкие безопасные обёртки. Сигнатуры
 * сверены по установленному SDK 3.11.8 (Веха 13.2).
 * ──────────────────────────────────────────────────────────────────────── */

// — BackButton —

export function mountBackButtonSafe(): void {
  mountBackButton.ifAvailable();
}

export function showBackButtonSafe(): void {
  showBackButton.ifAvailable();
}

export function hideBackButtonSafe(): void {
  hideBackButton.ifAvailable();
}

/**
 * Подписать обработчик на нажатие нативной кнопки «Назад». Возвращает функцию
 * отписки (или `undefined`, если кнопка недоступна) — вызвать в cleanup эффекта.
 */
export function onBackButtonClickSafe(fn: () => void): VoidFunction | undefined {
  if (!onBackButtonClick.isAvailable()) return undefined;
  return onBackButtonClick(fn);
}

export function offBackButtonClickSafe(fn: () => void): void {
  if (offBackButtonClick.isAvailable()) offBackButtonClick(fn);
}

// — MainButton —

export function mountMainButtonSafe(): void {
  mountMainButton.ifAvailable();
}

/**
 * Обновить параметры MainButton (текст/видимость/enabled/лоадер). Молча no-op,
 * если SDK недоступен или родитель ещё не смонтирован (`setParams` бросает
 * FunctionNotAvailableError до mount — глушим, т.к. хук монтирует кнопку первым
 * эффектом до эффекта параметров).
 */
export function setMainButtonParamsSafe(updates: Partial<MainButtonState>): void {
  if (!setMainButtonParams.isAvailable()) return;
  try {
    setMainButtonParams(updates);
  } catch {
    /* parent not mounted yet — следующий проход эффекта параметров выставит */
  }
}

export function onMainButtonClickSafe(fn: () => void): VoidFunction | undefined {
  if (!onMainButtonClick.isAvailable()) return undefined;
  return onMainButtonClick(fn);
}

export function offMainButtonClickSafe(fn: () => void): void {
  if (offMainButtonClick.isAvailable()) offMainButtonClick(fn);
}

// — Haptic feedback —

export function hapticImpact(style: ImpactHapticFeedbackStyle): void {
  if (hapticFeedbackImpactOccurred.isAvailable()) {
    hapticFeedbackImpactOccurred(style);
  }
}

export function hapticNotification(type: NotificationHapticFeedbackType): void {
  if (hapticFeedbackNotificationOccurred.isAvailable()) {
    hapticFeedbackNotificationOccurred(type);
  }
}

export function hapticSelection(): void {
  if (hapticFeedbackSelectionChanged.isAvailable()) {
    hapticFeedbackSelectionChanged();
  }
}
